import { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { invoicesApi, vehiclesApi, driversApi, customersApi } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import {
  Plus,
  FileText,
  Trash2,
  Loader2,
  Download,
  Eye,
  Edit,
  IndianRupee,
  CheckCircle2,
  Clock,
  AlertCircle,
  X,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 2,
  }).format(amount || 0);
};

const SERVICE_TYPES = ['rental', 'trip', 'package', 'transfer', 'other'];
const PAYMENT_MODES = ['cash', 'bank', 'upi', 'card', 'cheque'];

const initialLineItem = { description: '', hsn_sac: '996601', quantity: 1, rate: 0, amount: 0 };

const initialFormState = {
  customer_name: '',
  customer_address: '',
  customer_gstin: '',
  customer_phone: '',
  customer_email: '',
  invoice_date: new Date().toISOString().split('T')[0],
  due_date: '',
  vehicle_id: '',
  driver_id: '',
  service_type: 'rental',
  line_items: [{ ...initialLineItem }],
  subtotal: 0,
  cgst_rate: 9,
  sgst_rate: 9,
  igst_rate: 0,
  cgst_amount: 0,
  sgst_amount: 0,
  igst_amount: 0,
  total_tax: 0,
  grand_total: 0,
  terms_conditions: 'Payment is due within the specified due date. Late payments may attract interest.',
  notes: '',
  payment_status: 'pending',
};

const Invoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState(initialFormState);
  const [submitting, setSubmitting] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [useIGST, setUseIGST] = useState(false);

  const fetchData = async () => {
    try {
      const [invoicesRes, vehiclesRes, driversRes, customersRes] = await Promise.all([
        invoicesApi.getAll(),
        vehiclesApi.getAll(),
        driversApi.getAll(),
        customersApi.getAll(),
      ]);
      setInvoices(invoicesRes.data);
      setVehicles(vehiclesRes.data);
      setDrivers(driversRes.data);
      setCustomers(customersRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const calculateTotals = (items, cgstRate, sgstRate, igstRate, isIGST) => {
    const subtotal = items.reduce((sum, item) => sum + (item.amount || 0), 0);
    let cgst = 0, sgst = 0, igst = 0;
    
    if (isIGST) {
      igst = subtotal * (igstRate / 100);
    } else {
      cgst = subtotal * (cgstRate / 100);
      sgst = subtotal * (sgstRate / 100);
    }
    
    const totalTax = cgst + sgst + igst;
    const grandTotal = subtotal + totalTax;
    
    return { subtotal, cgst_amount: cgst, sgst_amount: sgst, igst_amount: igst, total_tax: totalTax, grand_total: grandTotal };
  };

  const handleLineItemChange = (index, field, value) => {
    const newItems = [...formData.line_items];
    newItems[index][field] = value;
    
    if (field === 'quantity' || field === 'rate') {
      newItems[index].amount = (parseFloat(newItems[index].quantity) || 0) * (parseFloat(newItems[index].rate) || 0);
    }
    
    const totals = calculateTotals(newItems, formData.cgst_rate, formData.sgst_rate, formData.igst_rate, useIGST);
    setFormData({ ...formData, line_items: newItems, ...totals });
  };

  const addLineItem = () => {
    setFormData({
      ...formData,
      line_items: [...formData.line_items, { ...initialLineItem }],
    });
  };

  const removeLineItem = (index) => {
    if (formData.line_items.length === 1) return;
    const newItems = formData.line_items.filter((_, i) => i !== index);
    const totals = calculateTotals(newItems, formData.cgst_rate, formData.sgst_rate, formData.igst_rate, useIGST);
    setFormData({ ...formData, line_items: newItems, ...totals });
  };

  const handleTaxRateChange = (field, value) => {
    const rate = parseFloat(value) || 0;
    const newData = { ...formData, [field]: rate };
    
    if (field === 'igst_rate') {
      newData.cgst_rate = 0;
      newData.sgst_rate = 0;
      setUseIGST(true);
    } else {
      newData.igst_rate = 0;
      setUseIGST(false);
    }
    
    const totals = calculateTotals(newData.line_items, newData.cgst_rate, newData.sgst_rate, newData.igst_rate, field === 'igst_rate');
    setFormData({ ...newData, ...totals });
  };

  const handleCustomerSelect = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    if (customer) {
      setFormData({
        ...formData,
        customer_name: customer.company_name || customer.contact_name,
        customer_address: customer.billing_address || '',
        customer_gstin: customer.gstin || '',
        customer_phone: customer.phone || '',
        customer_email: customer.email || '',
      });
    }
  };

  const handleEdit = (invoice) => {
    setEditingInvoice(invoice);
    setFormData({
      ...invoice,
      line_items: invoice.line_items || [{ ...initialLineItem }],
    });
    setUseIGST(invoice.igst_amount > 0);
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.customer_name || formData.line_items.length === 0) {
      toast.error('Please fill required fields');
      return;
    }

    setSubmitting(true);
    try {
      if (editingInvoice) {
        await invoicesApi.update(editingInvoice.id, formData);
        toast.success('Invoice updated successfully');
      } else {
        await invoicesApi.create(formData);
        toast.success('Invoice created successfully');
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData(initialFormState);
    setEditingInvoice(null);
    setUseIGST(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this invoice?')) return;
    try {
      await invoicesApi.delete(id);
      toast.success('Invoice deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete invoice');
    }
  };

  const handleDownloadPdf = async (invoice) => {
    try {
      const response = await invoicesApi.downloadPdf(invoice.id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Invoice_${invoice.invoice_number.replace(/\//g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Invoice downloaded');
    } catch (error) {
      toast.error('Failed to download invoice');
    }
  };

  const handleMarkPaid = async (invoice) => {
    try {
      await invoicesApi.updatePayment(invoice.id, 'paid', 'bank');
      toast.success('Invoice marked as paid');
      fetchData();
    } catch (error) {
      toast.error('Failed to update payment status');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-700 border-yellow-200',
      partial: 'bg-blue-100 text-blue-700 border-blue-200',
      paid: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    };
    const icons = {
      pending: <Clock className="w-3 h-3 mr-1" />,
      partial: <AlertCircle className="w-3 h-3 mr-1" />,
      paid: <CheckCircle2 className="w-3 h-3 mr-1" />,
    };
    return (
      <Badge className={`${styles[status] || styles.pending} flex items-center`}>
        {icons[status]}
        {status?.toUpperCase()}
      </Badge>
    );
  };

  // Summary calculations
  const totalInvoiced = invoices.reduce((sum, inv) => sum + (inv.grand_total || 0), 0);
  const totalPaid = invoices.filter(inv => inv.payment_status === 'paid').reduce((sum, inv) => sum + (inv.grand_total || 0), 0);
  const totalPending = invoices.filter(inv => inv.payment_status === 'pending').reduce((sum, inv) => sum + (inv.grand_total || 0), 0);

  if (loading) {
    return (
      <Layout title="Invoices">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout
      title="Invoices"
      subtitle="Generate & manage tax invoices"
      actions={
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button className="bg-[#D4AF37] hover:bg-[#B5952F] text-black" data-testid="create-invoice-btn">
              <Plus className="w-4 h-4 mr-2" />
              Create Invoice
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Playfair Display, serif' }}>
                {editingInvoice ? `Edit Invoice ${editingInvoice.invoice_number}` : 'Create New Invoice'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6 mt-4">
              {/* Customer Details */}
              <div className="space-y-4">
                <h3 className="font-semibold text-sm text-slate-500 uppercase">Customer Details</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Select Existing Customer</Label>
                    <Select onValueChange={handleCustomerSelect}>
                      <SelectTrigger>
                        <SelectValue placeholder="Choose customer" />
                      </SelectTrigger>
                      <SelectContent>
                        {customers.map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.company_name || c.contact_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Customer Name *</Label>
                    <Input
                      value={formData.customer_name}
                      onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                      placeholder="Customer/Company name"
                      required
                    />
                  </div>
                  <div className="space-y-2 col-span-2">
                    <Label>Billing Address</Label>
                    <Textarea
                      value={formData.customer_address}
                      onChange={(e) => setFormData({ ...formData, customer_address: e.target.value })}
                      placeholder="Full billing address"
                      rows={2}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Customer GSTIN</Label>
                    <Input
                      value={formData.customer_gstin}
                      onChange={(e) => setFormData({ ...formData, customer_gstin: e.target.value })}
                      placeholder="GSTIN (if applicable)"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Phone</Label>
                    <Input
                      value={formData.customer_phone}
                      onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
                      placeholder="Contact number"
                    />
                  </div>
                </div>
              </div>

              {/* Invoice Details */}
              <div className="space-y-4">
                <h3 className="font-semibold text-sm text-slate-500 uppercase">Invoice Details</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Invoice Date *</Label>
                    <Input
                      type="date"
                      value={formData.invoice_date}
                      onChange={(e) => setFormData({ ...formData, invoice_date: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Due Date *</Label>
                    <Input
                      type="date"
                      value={formData.due_date}
                      onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Service Type</Label>
                    <Select value={formData.service_type} onValueChange={(v) => setFormData({ ...formData, service_type: v })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {SERVICE_TYPES.map((type) => (
                          <SelectItem key={type} value={type} className="capitalize">{type}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Line Items */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-sm text-slate-500 uppercase">Line Items</h3>
                  <Button type="button" variant="outline" size="sm" onClick={addLineItem}>
                    <Plus className="w-4 h-4 mr-1" /> Add Item
                  </Button>
                </div>
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="w-[40%]">Description</TableHead>
                        <TableHead>HSN/SAC</TableHead>
                        <TableHead>Qty</TableHead>
                        <TableHead>Rate (₹)</TableHead>
                        <TableHead>Amount (₹)</TableHead>
                        <TableHead className="w-10"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {formData.line_items.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <Input
                              value={item.description}
                              onChange={(e) => handleLineItemChange(index, 'description', e.target.value)}
                              placeholder="Service description"
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              value={item.hsn_sac}
                              onChange={(e) => handleLineItemChange(index, 'hsn_sac', e.target.value)}
                              placeholder="996601"
                              className="w-24"
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              value={item.quantity}
                              onChange={(e) => handleLineItemChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                              className="w-16"
                              min="1"
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              value={item.rate}
                              onChange={(e) => handleLineItemChange(index, 'rate', parseFloat(e.target.value) || 0)}
                              className="w-24"
                            />
                          </TableCell>
                          <TableCell className="font-medium tabular-nums">
                            {formatCurrency(item.amount)}
                          </TableCell>
                          <TableCell>
                            {formData.line_items.length > 1 && (
                              <Button type="button" variant="ghost" size="sm" onClick={() => removeLineItem(index)}>
                                <X className="w-4 h-4 text-red-500" />
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>

              {/* Tax & Totals */}
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h3 className="font-semibold text-sm text-slate-500 uppercase">Tax Configuration</h3>
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        checked={!useIGST}
                        onChange={() => handleTaxRateChange('cgst_rate', 9)}
                      />
                      <span className="text-sm">CGST + SGST (Intra-state)</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        checked={useIGST}
                        onChange={() => handleTaxRateChange('igst_rate', 18)}
                      />
                      <span className="text-sm">IGST (Inter-state)</span>
                    </label>
                  </div>
                  {!useIGST ? (
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>CGST Rate (%)</Label>
                        <Input
                          type="number"
                          value={formData.cgst_rate}
                          onChange={(e) => handleTaxRateChange('cgst_rate', e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>SGST Rate (%)</Label>
                        <Input
                          type="number"
                          value={formData.sgst_rate}
                          onChange={(e) => handleTaxRateChange('sgst_rate', e.target.value)}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Label>IGST Rate (%)</Label>
                      <Input
                        type="number"
                        value={formData.igst_rate}
                        onChange={(e) => handleTaxRateChange('igst_rate', e.target.value)}
                        className="w-32"
                      />
                    </div>
                  )}
                </div>
                <div className="space-y-3 p-4 bg-slate-50 rounded-lg">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Subtotal:</span>
                    <span className="font-medium tabular-nums">{formatCurrency(formData.subtotal)}</span>
                  </div>
                  {!useIGST ? (
                    <>
                      <div className="flex justify-between">
                        <span className="text-slate-500">CGST ({formData.cgst_rate}%):</span>
                        <span className="tabular-nums">{formatCurrency(formData.cgst_amount)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">SGST ({formData.sgst_rate}%):</span>
                        <span className="tabular-nums">{formatCurrency(formData.sgst_amount)}</span>
                      </div>
                    </>
                  ) : (
                    <div className="flex justify-between">
                      <span className="text-slate-500">IGST ({formData.igst_rate}%):</span>
                      <span className="tabular-nums">{formatCurrency(formData.igst_amount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between pt-3 border-t border-slate-200">
                    <span className="font-bold">Grand Total:</span>
                    <span className="font-bold text-lg tabular-nums text-[#D4AF37]">{formatCurrency(formData.grand_total)}</span>
                  </div>
                </div>
              </div>

              {/* Notes & Terms */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Terms & Conditions</Label>
                  <Textarea
                    value={formData.terms_conditions}
                    onChange={(e) => setFormData({ ...formData, terms_conditions: e.target.value })}
                    rows={3}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Additional notes for customer"
                    rows={3}
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting} className="bg-[#D4AF37] hover:bg-[#B5952F] text-black">
                  {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  {editingInvoice ? 'Update Invoice' : 'Create Invoice'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      }
    >
      <div className="space-y-6" data-testid="invoices-page">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Invoices</p>
                  <p className="text-2xl font-bold text-slate-900 tabular-nums">{invoices.length}</p>
                </div>
                <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center">
                  <FileText className="w-6 h-6 text-slate-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Invoiced</p>
                  <p className="text-2xl font-bold text-blue-600 tabular-nums">{formatCurrency(totalInvoiced)}</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <IndianRupee className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Paid</p>
                  <p className="text-2xl font-bold text-emerald-600 tabular-nums">{formatCurrency(totalPaid)}</p>
                </div>
                <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                  <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Pending</p>
                  <p className="text-2xl font-bold text-yellow-600 tabular-nums">{formatCurrency(totalPending)}</p>
                </div>
                <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center">
                  <Clock className="w-6 h-6 text-yellow-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Invoices Table */}
        <Card>
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Playfair Display, serif' }}>All Invoices</CardTitle>
          </CardHeader>
          <CardContent>
            {invoices.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>No invoices created yet</p>
                <p className="text-sm mt-1">Click "Create Invoice" to get started</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Invoice No.</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Service</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoices.map((invoice) => (
                    <TableRow key={invoice.id} className="hover:bg-slate-50">
                      <TableCell className="font-medium">{invoice.invoice_number}</TableCell>
                      <TableCell>{invoice.invoice_date}</TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{invoice.customer_name}</p>
                          {invoice.customer_phone && (
                            <p className="text-sm text-slate-500">{invoice.customer_phone}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="capitalize">{invoice.service_type}</TableCell>
                      <TableCell className="text-right font-medium tabular-nums">
                        {formatCurrency(invoice.grand_total)}
                      </TableCell>
                      <TableCell>{getStatusBadge(invoice.payment_status)}</TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => handleDownloadPdf(invoice)} title="Download PDF">
                            <Download className="w-4 h-4 text-blue-500" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleEdit(invoice)} title="Edit">
                            <Edit className="w-4 h-4 text-slate-500" />
                          </Button>
                          {invoice.payment_status !== 'paid' && (
                            <Button variant="ghost" size="sm" onClick={() => handleMarkPaid(invoice)} title="Mark as Paid">
                              <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                            </Button>
                          )}
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(invoice.id)} title="Delete">
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default Invoices;
